import { useState } from "react";
import { ToolContainer } from "@/components/ToolLayout";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { convertLines, hasCombiningMarks, previewSource } from "./fancyFontPreview";
import { copyText } from "@/lib/copyText";
import { STYLES, DECORATIONS, convert, ZALGO_COUNTS, type ZalgoIntensity } from "./fancyStyles";

const TOOL_INFO_CONTENT = {
  what: `Converts regular text into stylized Unicode character variants — bold, italic, cursive, and other decorative styles — that you can copy and paste anywhere plain text is accepted, like a social media bio. Optionally wraps the result in a decorative symbol border.`,
  how: `Type your text (the greyed cards are only a sample until you do), browse the generated style variants, and copy the one you want to use. Pick a decoration from the row above the styles if you want a symbol or emoji border wrapped around every result.`,
  tip: `These styled characters are genuine Unicode symbols, not a font — they may look different or show as empty boxes in some apps and devices, and they won't be readable by screen readers the way normal text is, so use them sparingly in places accessibility matters.`,
};

export default function FancyFontGenerator() {
  const [input, setInput] = useState("");
  const [decoration, setDecoration] = useState(0);
  const [zalgoIntensity, setZalgoIntensity] = useState<ZalgoIntensity>(2);

  const { logUsageNow } = useLogUsageOnAction();
  const source = previewSource(input);

  const copy = async (text: string) => {
    const allowed = await logUsageNow("copy");
    if (!allowed) return;
    void copyText(text, "Copied!");
  };


  return (
    <ToolContainer>
      <div className="max-w-2xl mx-auto space-y-6">
        <Textarea
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Type something here"
          aria-label="Text to restyle"
          className="min-h-[80px] text-base"
        />

        {hasCombiningMarks(input) && (
          <p className="text-xs text-muted-foreground">Accented letters are styled as a letter plus a separate accent mark. Some apps, such as Instagram name fields, draw these badly, so check the result before you use it.</p>
        )}

        <div className="flex flex-wrap gap-1.5" role="group" aria-label="Decoration around every result">
          {DECORATIONS.map((dec, i) => (
            <Button
              key={dec.label}
              size="sm"
              aria-label={dec.name === "None" ? "No decoration" : `Wrap in ${dec.name} decoration`}
              aria-pressed={decoration === i}
              variant={decoration === i ? "default" : "outline"}
              onClick={() => setDecoration(i)}
              className="h-7 text-xs"
            >
              {dec.label}
            </Button>
          ))}
        </div>

        <div className="space-y-2">
          {STYLES.map(style => {
            const converted = convertLines(source.text, (line) => convert(line, style, { zalgoIntensity }));
            const result = converted ? DECORATIONS[decoration].wrap(converted) : converted;
            return (
              <div key={style.label} className="flex items-center justify-between gap-3 p-3 rounded-lg border bg-card">
                <div className="min-w-0">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">{style.label}</p>
                  <p className={`text-base break-words whitespace-pre-line ${style.id === "zalgo" ? "overflow-hidden py-3 leading-loose" : ""} ${source.isSample ? "text-muted-foreground/60" : ""}`}>{result || "\u00a0"}</p>
                  {style.id === "zalgo" && (
                    <div className="mt-1 flex items-center gap-1.5" role="group" aria-label="Zalgo intensity">
                      <span className="text-xs text-muted-foreground">Intensity</span>
                      {([1, 2, 3] as ZalgoIntensity[]).map(n => (
                        <Button key={n} size="sm" variant={zalgoIntensity === n ? "default" : "outline"} aria-pressed={zalgoIntensity === n} aria-label={`Zalgo intensity ${n} of 3 (${ZALGO_COUNTS[n].up + ZALGO_COUNTS[n].mid + ZALGO_COUNTS[n].down} marks per letter)`} onClick={() => setZalgoIntensity(n)} className="h-6 w-7 px-0 text-xs">{n}</Button>
                      ))}
                    </div>
                  )}
                  {style.hint && <p className="mt-1 text-xs text-muted-foreground" data-testid="style-hint">{style.hint}</p>}
                </div>
                <Button size="sm" variant="ghost" aria-label={`Copy ${style.label}`} disabled={source.isSample} title={source.isSample ? "Type your own text first" : undefined} onClick={() => copy(result)} className="shrink-0 gap-1">
                  <Copy className="h-4 w-4" /> Copy
                </Button>
              </div>
            );
          })}
        </div>
      </div>
          <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
